import { ClaimStatus, Filters } from '@/api/claims'
import { CompaniesListResponse } from '@/api/company'
import {
  BackComponent,
  Column,
  PageMainContent,
  Row,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { useDebouncedCallback } from '@/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { ActionBar } from './ActionBar'
import ClaimsPageError from './Error'
import { SkeletonLoader } from './Skeleton'
import { Container } from './styles'
import ClaimsTable from './Table'
import { useCompaniesClaims } from './useCompaniesClaims'

export const ClaimsPage = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<Filters>({
    status: [] as ClaimStatus[],
    insuranceCategories: [] as string[],
    date: { from: undefined, to: undefined },
  })

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedCallback((input: string) => {
    setSearch(input)
  }, 200)

  const { companies } = useOutletContext<{ companies: CompaniesListResponse }>()

  const { claimsMap, isLoading, isError } = useCompaniesClaims(
    search,
    filters,
    companies,
  )

  const hasData = Object.values(claimsMap).some(
    (locations) => locations.length > 0,
  )

  return (
    <PageMainContent>
      <Container>
        <Column gap="8px">
          <BackComponent noContentGutter noMargin />
          <Typography bold variant="h3">
            {t('claimsPage.title')}
          </Typography>
          <ActionBar setSearch={debouncedSearch} applyFilters={setFilters} />

          {isError && <ClaimsPageError />}

          {isLoading && <SkeletonLoader />}

          {!hasData && !isLoading && !isError && (
            <Container>
              <Typography color={ThemeColor.glass500} variant="p1Body">
                {t('claimsPage.noData')}
              </Typography>
            </Container>
          )}

          <Column gap="24px">
            {claimsMap &&
              Object.entries(claimsMap).map(([company, locations], index) => {
                const [, companyName] = company.split('/')

                return (
                  locations.length > 0 && (
                    <Container key={index}>
                      <Row marginTop="18px">
                        <Typography bold variant="h4">
                          {companyName}
                        </Typography>
                      </Row>
                      {locations.map((location) => (
                        <ClaimsTable
                          key={location.locationName}
                          location={location}
                        />
                      ))}
                    </Container>
                  )
                )
              })}
          </Column>
        </Column>
      </Container>
    </PageMainContent>
  )
}
